import { parseWithZod } from '@conform-to/zod';
import { invariantResponse } from '@epic-web/invariant';
import { useHints } from 'app/utils/client-hints';
import { z } from 'zod';
import { useRequestInfo } from '~/utils/request-info';
import { setTheme } from '~/utils/theme.server';

import { type ActionFunctionArgs, json } from '@remix-run/node';
import { useFetchers } from '@remix-run/react';

const ThemeFormSchema = z.object({
  theme: z.enum(['system', 'light', 'dark']),
});

/**
 * @returns the client hint theme.
 */
export function useTheme() {
  const hints = useHints();
  const requestInfo = useRequestInfo();
  const optimisticMode = useOptimisticThemeMode();

  let theme;
  if (optimisticMode) {
    theme = optimisticMode === 'system' ? hints.theme : optimisticMode;
  } else {
    theme = requestInfo.userPrefs.theme ?? hints.theme;
  }

  // Return multiple values as an object
  return {
    theme,
    themeName: optimisticMode ?? requestInfo.userPrefs.theme,
  };
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const submission = parseWithZod(formData, { schema: ThemeFormSchema });

  invariantResponse(submission.status === 'success', 'Invalid theme received');

  const { theme } = submission.value;

  const responseInit = {
    headers: { 'set-cookie': setTheme(theme) },
  };
  return json({ result: submission.reply() }, responseInit);
}

export function useOptimisticThemeMode() {
  const fetchers = useFetchers();
  const themeFetcher = fetchers.find(
    (f) => f.formAction === '/resources/theme-switch',
  );

  if (themeFetcher && themeFetcher.formData) {
    const submission = parseWithZod(themeFetcher.formData, {
      schema: ThemeFormSchema,
    });

    if (submission.status === 'success') {
      return submission.value.theme;
    }
  }
}
